/** Common small components used across pages. */
import { Link } from 'react-router-dom';
import {
  CircleNotchIcon,
  WarningOctagonIcon,
} from '@phosphor-icons/react';

export function Spinner({ size = 24, label }) {
  return (
    <div className="inline-flex items-center gap-2" data-testid="spinner">
      <CircleNotchIcon size={size} weight="bold" className="gl-spin" style={{ color: 'var(--gl-navy)' }} />
      {label && <span className="text-sm" style={{ color: 'var(--gl-muted)' }}>{label}</span>}
    </div>
  );
}

export function PageHeader({ eyebrow, title, subtitle, right }) {
  return (
    <header className="px-5 pt-6 pb-4 md:px-8 md:pt-8" data-testid="page-header">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          {eyebrow && (
            <div className="gl-eyebrow" style={{ color: 'var(--gl-navy)', marginBottom: '0.5rem' }} data-testid="page-eyebrow">
              {eyebrow}
            </div>
          )}
          <h1
            className="font-bold text-3xl md:text-4xl leading-tight tracking-tight"
            style={{ color: 'var(--gl-dark)', fontFamily: 'Inter, system-ui, sans-serif', letterSpacing: '-0.02em' }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-2 text-sm md:text-base" style={{ color: 'var(--gl-muted)', maxWidth: '60ch' }}>
              {subtitle}
            </p>
          )}
        </div>
        {right && <div className="flex items-center gap-3">{right}</div>}
      </div>
    </header>
  );
}

export function EmptyState({ icon: Icon = WarningOctagonIcon, title, body, action }) {
  return (
    <div
      className="gl-card flex flex-col items-center text-center py-12 px-6"
      style={{ borderStyle: 'dashed' }}
      data-testid="empty-state"
    >
      <div
        className="w-14 h-14 mb-4 flex items-center justify-center"
        style={{ background: 'var(--gl-dark-navy)', color: 'var(--gl-gold)' }}
      >
        <Icon size={28} weight="fill" />
      </div>
      <div className="font-display font-extrabold uppercase text-xl" style={{ color: 'var(--gl-dark)' }}>
        {title}
      </div>
      <div className="mt-2 text-sm" style={{ color: 'var(--gl-muted)', maxWidth: '40ch' }}>
        {body}
      </div>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function PriorityChip({ value }) {
  const cls = value === 'P1' ? 'gl-chip-p1' : value === 'P2' ? 'gl-chip-p2' : 'gl-chip-p3';
  return <span className={`gl-chip ${cls}`} data-testid={`priority-chip-${value}`}>{value}</span>;
}

export function CarryoverChip() {
  return (
    <span className="gl-tag gl-tag-gold" data-testid="carryover-chip">
      carry-over
    </span>
  );
}

export function ResolvedChip() {
  return (
    <span className="gl-tag gl-tag-p3 gl-tag-dot" data-testid="resolved-chip">
      resolved
    </span>
  );
}

export function StatusChip({ value }) {
  const map = {
    in_progress: { tone: 'navy', label: 'in-progress' },
    report_ready: { tone: 'gold', label: 'report-ready' },
    delivered: { tone: 'p3', label: 'delivered' },
    archived: { tone: 'neutral', label: 'archived' },
  };
  const cfg = map[value] || map.in_progress;
  const toneClass = cfg.tone === 'neutral' ? '' : `gl-tag-${cfg.tone}`;
  return (
    <span
      className={`gl-tag ${toneClass} gl-tag-dot`}
      data-testid={`status-chip-${value}`}
    >
      {cfg.label}
    </span>
  );
}

export function EngagementTypeBadge({ type }) {
  const label =
    type === 'walkthrough' ? 'gemba-walk' :
    type === 'gap_check' ? 'doc-review' :
    type === 'incident_investigation' ? 'incident' :
    'combined';
  return (
    <span className="gl-tag gl-tag-dark" data-testid={`type-badge-${type}`}>
      {label}
    </span>
  );
}


/* =====================================================================
   GL-DESIGN-2026Q2 — Editorial SaaS / Operator UI primitives.
   Drop-in components for KPI tiles, eyebrows, and tag pills so every
   screen pulls from the same vocabulary.
   ===================================================================== */

/**
 * <Eyebrow> — small uppercase mono label. Sit it above a headline or a
 * KPI value to add context. Color defaults to muted gray; override with
 * `tone="gold"` for marketing emphasis, `tone="navy"` for data emphasis.
 */
export function Eyebrow({ children, tone = 'muted', testid, className = '', style = {} }) {
  const color =
    tone === 'gold' ? 'var(--gl-gold)' :
    tone === 'navy' ? 'var(--gl-navy)' :
    tone === 'p1' ? 'var(--gl-p1)' :
    tone === 'p3' ? 'var(--gl-p3)' :
    '#6b7280';
  return (
    <div
      className={`gl-eyebrow ${className}`}
      style={{ color, ...style }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/**
 * <KpiTile> — Plausible-style stat tile.
 *   <KpiTile label="Active" value={12} sublabel="0 last 7d" />
 * Tones (semantic only — leave default for plain white):
 *   'navy' | 'gold' | 'p1' | 'p2' | 'p3'
 * Stripe accent (Risk Breakdown pattern): 'p1' | 'p2' | 'p3' | 'navy' | 'gold'
 * Value can be colored separately via valueTone.
 */
export function KpiTile({
  label,
  value,
  sublabel,
  delta,
  deltaTone,         // 'pos' | 'neg' | 'neutral'
  tone,              // background tint
  valueTone,         // color tint on the number itself
  stripe,            // left-accent stripe
  big = false,
  to,
  onClick,
  active = false,
  testid,
}) {
  const toneClass =
    tone === 'navy' ? 'gl-kpi-tinted-navy' :
    tone === 'gold' ? 'gl-kpi-tinted-gold' :
    tone === 'p1' ? 'gl-kpi-tinted-p1' :
    tone === 'p2' ? 'gl-kpi-tinted-p2' :
    tone === 'p3' ? 'gl-kpi-tinted-p3' :
    '';
  const valueToneClass =
    valueTone === 'gold' ? 'gl-kpi-value-gold' :
    valueTone === 'navy' ? 'gl-kpi-value-navy' :
    valueTone === 'p1' ? 'gl-kpi-value-p1' :
    valueTone === 'p2' ? 'gl-kpi-value-p2' :
    valueTone === 'p3' ? 'gl-kpi-value-p3' :
    '';
  const stripeClass = stripe ? `gl-kpi-stripe gl-kpi-stripe-${stripe}` : '';
  const deltaClass =
    deltaTone === 'pos' ? 'gl-kpi-delta-pos' :
    deltaTone === 'neg' ? 'gl-kpi-delta-neg' :
    '';
  const body = (
    <>
      <div className="gl-kpi-label">{label}</div>
      <div className={`gl-kpi-value ${big ? 'gl-kpi-value-lg' : ''} ${valueToneClass}`}>{value}</div>
      {sublabel && <div className="gl-kpi-sublabel">{sublabel}</div>}
      {delta && <div className={`gl-kpi-delta ${deltaClass}`}>{delta}</div>}
    </>
  );
  const baseStyle = active
    ? { borderColor: 'var(--gl-dark)', borderWidth: 1, boxShadow: '0 0 0 3px var(--gl-tint-gold)' }
    : undefined;
  const cls = `gl-kpi ${toneClass} ${stripeClass} block`;
  if (to) {
    return <Link to={to} className={cls} style={baseStyle} data-testid={testid}>{body}</Link>;
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${cls} text-left w-full`}
        style={{ ...(baseStyle || {}), cursor: 'pointer' }}
        data-testid={testid}
        aria-pressed={active}
      >
        {body}
      </button>
    );
  }
  return <div className={cls} data-testid={testid}>{body}</div>;
}

/**
 * <SectionCard> — Plausible-style grouping wrapper. Wraps a set of KPI
 * tiles with a soft category tint + right-aligned mono eyebrow tag.
 *   <SectionCard title="Quick-Contact Micro-Form" eyebrow="LOW-FRICTION PATH" tone="gold">
 *     <KpiTile ... />
 *     <KpiTile ... />
 *   </SectionCard>
 */
export function SectionCard({ title, eyebrow, eyebrowTone = 'muted', tone, children, annotation, testid, className = '' }) {
  const toneClass =
    tone === 'gold' ? 'gl-section-card-gold' :
    tone === 'navy' ? 'gl-section-card-navy' :
    tone === 'p3' ? 'gl-section-card-p3' :
    tone === 'neutral' ? 'gl-section-card-neutral' :
    '';
  const eyebrowToneClass =
    eyebrowTone === 'gold' ? 'gl-card-eyebrow-gold' :
    eyebrowTone === 'navy' ? 'gl-card-eyebrow-navy' :
    eyebrowTone === 'p3' ? 'gl-card-eyebrow-p3' :
    '';
  return (
    <div className={`gl-section-card ${toneClass} ${className}`} data-testid={testid}>
      {(title || eyebrow) && (
        <div className="flex items-start justify-between gap-3 mb-4">
          {title && <div className="gl-card-headline">{title}</div>}
          {eyebrow && (
            <div className={`gl-card-eyebrow ${eyebrowToneClass}`}>{eyebrow}</div>
          )}
        </div>
      )}
      {children}
      {annotation && <p className="gl-annotation mt-3">{annotation}</p>}
    </div>
  );
}

/**
 * <TagPill> — kebab-style mono tag. Use for status / category /
 * metadata where the old serif chips were over-shouting.
 *   <TagPill tone="navy">in-progress</TagPill>
 */
export function TagPill({ children, tone = 'neutral', dot = false, testid, title, className = '' }) {
  const toneClass =
    tone === 'navy' ? 'gl-tag-navy' :
    tone === 'gold' ? 'gl-tag-gold' :
    tone === 'p1' ? 'gl-tag-p1' :
    tone === 'p2' ? 'gl-tag-p2' :
    tone === 'p3' ? 'gl-tag-p3' :
    tone === 'dark' ? 'gl-tag-dark' :
    '';
  return (
    <span
      className={`gl-tag ${toneClass} ${dot ? 'gl-tag-dot' : ''} ${className}`}
      data-testid={testid}
      title={title}
    >
      {children}
    </span>
  );
}

/**
 * <SectionHeader> — eyebrow + serif headline pair. Use to introduce a
 * content block. Cheaper than <PageHeader> (which is for the screen).
 */
export function SectionHeader({ eyebrow, title, right, className = '' }) {
  return (
    <div className={`flex items-end justify-between gap-3 flex-wrap mb-4 ${className}`}>
      <div>
        {eyebrow && <div className="gl-section-eyebrow">{eyebrow}</div>}
        {title && <div className="gl-section-headline">{title}</div>}
      </div>
      {right && <div className="flex items-center gap-2">{right}</div>}
    </div>
  );
}

/**
 * <Annotation> — small italic editorial line. Use under tables / KPIs
 * to add one-sentence interpretation.
 */
export function Annotation({ children, className = '' }) {
  return <p className={`gl-annotation ${className}`}>{children}</p>;
}
